import { useMemo, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { PageHeader } from '../../components/PageHeader';
import { FILTER_LABELS, REPORT_CATEGORIES, ReportDefinition } from './reportCatalog';
import { ReportRunDialog } from './ReportRunDialog';

/** Reports — grouped by area, searchable. Opening a report shows its
 * filters and offers Preview PDF (branded, in-app viewer), Excel and CSV. */
export const ReportsPage = () => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<ReportDefinition | null>(null);

  const categories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return REPORT_CATEGORIES.map((c) => ({
      ...c,
      reports: c.reports.filter((r) => !q || `${r.name} ${r.description} ${c.title}`.toLowerCase().includes(q)),
    })).filter((c) => c.reports.length > 0);
  }, [search]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        icon={AssessmentOutlinedIcon}
        color="#5B2DA3"
        title="Reports"
        subtitle="Choose a report, set its filters, then preview the branded PDF or export to Excel or CSV."
      />

      <TextField
        placeholder="Search reports…"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3, width: { xs: '100%', sm: 360 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {categories.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No reports match “{search}”.
        </Typography>
      )}

      <Stack spacing={4}>
        {categories.map((category) => (
          <Box key={category.key} component="section" aria-labelledby={`reports-${category.key}`}>
            <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1.5 }} flexWrap="wrap">
              <Typography id={`reports-${category.key}`} variant="h6" sx={{ fontWeight: 700 }}>
                {category.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {category.description}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              }}
            >
              {category.reports.map((report) => {
                const Icon = report.icon;
                return (
                  <Card key={report.type} variant="outlined" sx={{ height: '100%', '&:hover': { borderColor: category.color } }}>
                    <CardActionArea onClick={() => setOpen(report)} sx={{ height: '100%', alignItems: 'stretch' }} aria-label={`Open ${report.name}`}>
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: `${category.color}1A`,
                              color: category.color,
                              flexShrink: 0,
                            }}
                          >
                            <Icon />
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {report.name}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                          {report.description}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                          {report.filters.slice(0, 4).map((f) => (
                            <Chip key={f} size="small" variant="outlined" label={FILTER_LABELS[f]} />
                          ))}
                          {report.filters.length > 4 && <Chip size="small" variant="outlined" label={`+${report.filters.length - 4}`} />}
                          <Box sx={{ flexGrow: 1 }} />
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: category.color }}>
                            <PictureAsPdfIcon fontSize="small" />
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              PDF · Excel · CSV
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <ReportRunDialog report={open} onClose={() => setOpen(null)} />
    </Box>
  );
};
